// frontend/src/pages/admin/CVGeneration/CertificatesSection.js
import React, { useState, useEffect } from 'react';
import { 
  Award, Plus, Edit2, Trash2, X, AlertTriangle, Clock, Save, AlertCircle
} from 'lucide-react';
import { staffAPI } from '../../../services/api';
import toast from '../../../utils/toast';
import Portal from '../../../components/UI/Portal';
import FormSelect from '../../../components/UI/FormSelect';
import DatePickerField from '../../../components/UI/DatePickerField';
import { formatDate, getDataFreshness, toLocalISODate, parseLocalDate } from '../../../utils/dateUtils';

/**
 * Certificates Section for CV Data Entry
 * 
 * Features:
 * - Display professional certificate records in a table
 * - Add/Edit/Delete with modals
 * - Duplicate detection (blocks save if duplicate found)
 * - Data freshness indicators
 * - Expiry status indicators
 */
const CertificatesSection = ({ staffId, records = [], onDataChange }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    cert_name: '',
    cert_short_name: '',
    cert_issuing_organization: '',
    cert_category: 'construction',
    cert_issue_date: '',
    cert_expiry_date: '',
    cert_is_lifetime: false,
    cert_certificate_number: '',
  });

  // Category options
  const categoryOptions = [
    { value: 'safety', label: 'Safety & Health' },
    { value: 'construction', label: 'Construction & Building' },
    { value: 'project_management', label: 'Project Management' },
    { value: 'quality', label: 'Quality Management' },
    { value: 'environmental', label: 'Environmental' },
    { value: 'it_tech', label: 'IT & Technology' },
    { value: 'finance', label: 'Finance & Accounting' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'legal', label: 'Legal & Compliance' },
    { value: 'trade_skill', label: 'Trade Skills' },
    { value: 'multi_skilling', label: 'Multi-Skilling' },
    { value: 'other', label: 'Other' },
  ];

  // Reset form when modal opens/closes
  useEffect(() => {
    if (showModal && editingRecord) {
      setFormData({
        cert_name: editingRecord.cert_name || '',
        cert_short_name: editingRecord.cert_short_name || '',
        cert_issuing_organization: editingRecord.cert_issuing_organization || '',
        cert_category: editingRecord.cert_category || 'construction',
        cert_issue_date: editingRecord.cert_issue_date || '',
        cert_expiry_date: editingRecord.cert_expiry_date || '',
        cert_is_lifetime: editingRecord.cert_is_lifetime || false,
        cert_certificate_number: editingRecord.cert_certificate_number || '',
      });
    } else if (showModal) {
      setFormData({
        cert_name: '',
        cert_short_name: '',
        cert_issuing_organization: '',
        cert_category: 'construction',
        cert_issue_date: '',
        cert_expiry_date: '',
        cert_is_lifetime: false,
        cert_certificate_number: '',
      });
    }
    setDuplicateWarning(null);
  }, [showModal, editingRecord]);

  // Check for duplicates (same cert name + issuing organization)
  const checkDuplicate = () => {
    const isDuplicate = records.some(record => {
      // Skip the record being edited
      if (editingRecord && record.id === editingRecord.id) return false;
      
      const sameName = record.cert_name?.toLowerCase() === formData.cert_name?.toLowerCase();
      const sameOrg = record.cert_issuing_organization?.toLowerCase() === formData.cert_issuing_organization?.toLowerCase();
      
      return sameName && sameOrg;
    });
    
    if (isDuplicate) {
      setDuplicateWarning('A similar certificate already exists (same name and issuing organization).');
      return true;
    }
    setDuplicateWarning(null);
    return false;
  };

  const handleOpenAdd = () => {
    setEditingRecord(null);
    setShowModal(true);
  };

  const handleOpenEdit = (record) => {
    setEditingRecord(record);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRecord(null);
    setDuplicateWarning(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for duplicates
    if (checkDuplicate()) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      const payload = {
        cert_staff_id: staffId,
        cert_name: formData.cert_name,
        cert_short_name: formData.cert_short_name || formData.cert_name.substring(0, 10),
        cert_issuing_organization: formData.cert_issuing_organization,
        cert_category: formData.cert_category,
        cert_issue_date: formData.cert_issue_date,
        cert_expiry_date: formData.cert_is_lifetime ? null : formData.cert_expiry_date || null,
        cert_is_lifetime: formData.cert_is_lifetime,
        cert_certificate_number: formData.cert_certificate_number,
      };

      if (editingRecord) {
        await staffAPI.updateCertificate(editingRecord.id, payload);
        toast.success('Certificate updated successfully');
      } else {
        await staffAPI.createCertificate(payload);
        toast.success('Certificate added successfully');
      }
      
      handleCloseModal();
      onDataChange();
    } catch (error) {
      console.error('Error saving certificate:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          'Failed to save certificate';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (recordId) => {
    try {
      await staffAPI.deleteCertificate(recordId);
      toast.success('Certificate deleted successfully');
      setDeleteConfirm(null);
      onDataChange();
    } catch (error) {
      console.error('Error deleting certificate:', error);
      toast.error('Failed to delete certificate');
    }
  };

  // Data freshness calculation is now handled by getDataFreshness utility from dateUtils

  // Get expiry status
  const getExpiryStatus = (expiryDate, isLifetime) => {
    if (isLifetime) {
      return { label: 'Lifetime', className: 'bg-green-100 text-green-700', isExpired: false };
    }
    if (!expiryDate) {
      return { label: 'No Expiry', className: 'bg-gray-100 text-gray-600', isExpired: false };
    }
    
    const expiry = new Date(expiryDate);
    const now = new Date();
    const diffDays = Math.floor((expiry - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { label: 'Expired', className: 'bg-red-100 text-red-700', isExpired: true };
    }
    if (diffDays < 30) {
      return { label: `Expires in ${diffDays} days`, className: 'bg-amber-100 text-amber-700', isExpired: false };
    }
    if (diffDays < 90) {
      return { label: `Expires in ${Math.floor(diffDays / 30)} months`, className: 'bg-yellow-100 text-yellow-700', isExpired: false };
    }
    return { label: formatDate(expiryDate), className: 'bg-green-100 text-green-700', isExpired: false };
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Section Header */}
      <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Award className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Professional Certificates</h3>
            <p className="text-xs sm:text-sm text-gray-500 truncate">{records.length} record{records.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button
          onClick={handleOpenAdd}
          className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 btn-sm flex-shrink-0"
          aria-label="Add Certificate"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Add Certificate</span>
        </button>
      </div>

      {/* Records Table */}
      {records.length === 0 ? (
        <div className="px-6 py-8 text-center text-gray-500">
          <Award className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">No professional certificates yet</p>
          <button
            onClick={handleOpenAdd}
            className="mt-3 text-sm text-red-600 hover:text-red-700 font-medium"
          >
            + Add first certificate
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Certificate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Issuing Organization</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Expiry</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Last Modified</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {records.map((record) => {
                const freshness = getDataFreshness(record.cert_updated_at);
                const expiryStatus = getExpiryStatus(record.cert_expiry_date, record.cert_is_lifetime);
                return (
                  <tr key={record.id} className={`table-row ${expiryStatus.isExpired ? 'bg-red-50/30' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-gray-900">
                          {record.cert_name || 'N/A'}
                        </div>
                        {expiryStatus.isExpired && (
                          <AlertCircle className="w-4 h-4 text-red-500" />
                        )}
                      </div>
                      {record.cert_short_name && (
                        <div className="text-xs text-gray-500">{record.cert_short_name}</div>
                      )}
                      <div className="text-xs text-gray-400 capitalize mt-0.5">
                        {record.cert_category?.replace('_', ' ')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {record.cert_issuing_organization || 'N/A'}
                      </div>
                      {record.cert_certificate_number && (
                        <div className="text-xs text-gray-500">#{record.cert_certificate_number}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${expiryStatus.className}`}>
                        {expiryStatus.label}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`flex items-center gap-1 text-xs ${freshness.isStale ? 'text-amber-600' : 'text-gray-500'}`}>
                        <Clock className="w-3 h-3" />
                        {freshness.label}
                        {freshness.isStale && <AlertTriangle className="w-3 h-3 ml-1" />}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenEdit(record)}
                          className="icon-button-admin"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(record.id)}
                          className="icon-button-danger"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Another Row (Footer) */}
      {records.length > 0 && (
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <button
            onClick={handleOpenAdd}
            className="btn btn-admin-outline btn-sm w-full"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Another Certificate
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <Portal>
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-container modal-container-md" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">
                  {editingRecord ? 'Edit Certificate' : 'Add Certificate'}
                </h3>
                <button onClick={handleCloseModal} className="modal-close-admin">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-content space-y-4">
                  {/* Duplicate Warning */}
                  {duplicateWarning && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Duplicate Detected</p>
                        <p className="text-sm text-red-700">{duplicateWarning}</p>
                      </div>
                    </div>
                  )}

                  {/* Certificate Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Certificate Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="input-admin w-full"
                      value={formData.cert_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, cert_name: e.target.value }))}
                      placeholder="e.g., Certified Safety Coordinator for Project Management"
                      required
                    />
                  </div>

                  {/* Short Name & Category */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Short Name / Abbreviation
                      </label>
                      <input
                        type="text"
                        className="input-admin w-full"
                        value={formData.cert_short_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, cert_short_name: e.target.value }))}
                        placeholder="e.g., CSCPM"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category <span className="text-red-500">*</span>
                      </label>
                      <FormSelect
                        options={categoryOptions}
                        value={formData.cert_category}
                        onChange={(val) => setFormData(prev => ({ ...prev, cert_category: val }))}
                        theme="admin"
                        required
                      />
                    </div>
                  </div>

                  {/* Issuing Organization */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Issuing Organization <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="input-admin w-full"
                      value={formData.cert_issuing_organization}
                      onChange={(e) => setFormData(prev => ({ ...prev, cert_issuing_organization: e.target.value }))}
                      placeholder="e.g., WSH Council, BCA, MOM"
                      required
                    />
                  </div>

                  {/* Certificate Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Certificate Number
                    </label>
                    <input
                      type="text"
                      className="input-admin w-full"
                      value={formData.cert_certificate_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, cert_certificate_number: e.target.value }))}
                      placeholder="e.g., CSCPM-2024-12345"
                    />
                  </div>

                  {/* Date Range */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Issue Date <span className="text-red-500">*</span>
                      </label>
                      <DatePickerField
                        selected={parseLocalDate(formData.cert_issue_date)}
                        onChange={(date) => setFormData(prev => ({ ...prev, cert_issue_date: date ? toLocalISODate(date) : '' }))}
                        className="input-admin pl-10 w-full"
                        placeholderText="Select date"
                        maxDate={parseLocalDate(formData.cert_expiry_date)}
                        portalId="datepicker-portal"
                        popperClassName="datepicker-popper-high-z"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expiry Date
                      </label>
                      <DatePickerField
                        selected={parseLocalDate(formData.cert_expiry_date)}
                        onChange={(date) => setFormData(prev => ({ ...prev, cert_expiry_date: date ? toLocalISODate(date) : '' }))}
                        className="input-admin pl-10 w-full"
                        placeholderText="Select date"
                        minDate={parseLocalDate(formData.cert_issue_date)}
                        disabled={formData.cert_is_lifetime}
                        portalId="datepicker-portal"
                        popperClassName="datepicker-popper-high-z"
                        isClearable
                      />
                    </div>
                  </div>

                  {/* Lifetime Certificate Checkbox */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="cert_is_lifetime"
                      className="w-4 h-4 text-red-600 border-gray-300 rounded focus-visible:ring-red-500"
                      checked={formData.cert_is_lifetime}
                      onChange={(e) => setFormData(prev => ({ ...prev, cert_is_lifetime: e.target.checked }))}
                    />
                    <label htmlFor="cert_is_lifetime" className="text-sm text-gray-700">
                      This certificate is valid for lifetime (no expiry)
                    </label>
                  </div>

                  {/* Edit Mode: Last Modified Info */}
                  {editingRecord && (
                    <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                      Last modified: {formatDate(editingRecord.cert_updated_at)}
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn btn-admin-outline"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-admin-primary"
                    disabled={isSubmitting || duplicateWarning}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="loading-spinner-sm mr-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {editingRecord ? 'Update' : 'Add'} Certificate
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Portal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <Portal>
          <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
            <div className="modal-container modal-container-sm" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Delete Certificate</h3>
                <button onClick={() => setDeleteConfirm(null)} className="modal-close-admin">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="modal-content">
                <p className="text-sm text-gray-700">
                  Are you sure you want to delete this certificate? This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="btn btn-admin-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="btn btn-danger"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};

export default CertificatesSection;

